import React, { useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import IconFont from "../elements/IconFont";
import useWorkspaceAccess from "../hooks/workspace/useWorkspaceAccess";
import { appRoutes } from "../../routes/appRoutes";
import ReactTooltip from "./ReactTooltip";

type DrawerNavProps = {
  isDesktopCollapsed: boolean;
};

const DrawerNav: React.FC<DrawerNavProps> = ({ isDesktopCollapsed }) => {
  const { pathname } = useLocation();
  const { canViewBilling } = useWorkspaceAccess();
  const navLinks = useMemo(() => {
    const links: Array<{
      to: string;
      relations: string[];
      label: string;
      children: React.ReactNode;
    }> = [
      {
        to: appRoutes.experiments.root,
        relations: [],
        label: "Experiments",
        children: (
          <>
            <IconFont name="flask-conical" className="text-[22px]" />
            <span className="ui-nav-label">Experiments</span>
          </>
        ),
      },
      {
        to: appRoutes.settings.root,
        relations: [],
        label: "Settings",
        children: (
          <>
            <IconFont name="settings" className="text-[22px]" />
            <span className="ui-nav-label">Settings</span>
          </>
        ),
      },
    ];

    if (canViewBilling) {
      links.splice(1, 0, {
        to: appRoutes.billing.root,
        relations: [appRoutes.billing.success, appRoutes.billing.failed],
        label: "Billing",
        children: (
          <>
            <IconFont name="credit-card" className="text-[22px]" />
            <span className="ui-nav-label">Billing</span>
          </>
        ),
      });
    }

    return links;
  }, [canViewBilling]);
  return (
    <nav className="flex w-full flex-col gap-[16px] bg-background-surface-01 pointer-events-auto">
      {navLinks.map((link, index) => {
        const tooltipId = `drawer-nav-${link.label.toLowerCase()}`;
        return (
          <React.Fragment key={"navlink-" + index}>
            <NavLink
              id={tooltipId}
              to={link.to}
              title={isDesktopCollapsed ? undefined : link.label}
              aria-label={link.label}
              className={`ui-nav-item ${pathname === link.to || link.relations.includes(pathname) || ((link.to === appRoutes.experiments.root || link.to === appRoutes.settings.root) && pathname.startsWith(`${link.to}/`)) ? "ui-nav-item-active" : "ui-nav-item-idle"}`}
            >
              {link.children}
            </NavLink>
            {isDesktopCollapsed && (
              <ReactTooltip anchorSelect={`#${tooltipId}`} place="right">
                {link.label}
              </ReactTooltip>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default DrawerNav;
